import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { DocMutationService } from '@affine/core/modules/doc';
import { OBJECT_ROLE_LABEL_KEYS } from '@affine/core/modules/permissions/utils/object-role-labels';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  DocRole,
  getDocGroupRolesQuery,
  getGroupsQuery,
  getMembersByWorkspaceIdQuery,
  type GetPageGrantedUsersListQuery,
  getPageGrantedUsersListQuery,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { type MutableRefObject, useEffect, useMemo, useRef } from 'react';

import { PermissionEditor } from '../shared/permission-editor';
import type {
  PermissionEditorAdapter,
  PermissionEditorChangeEvent,
  PermissionEditorConfig,
  PermissionEntry,
} from '../shared/permission-editor-types';

type GrantedUser = NonNullable<
  GetPageGrantedUsersListQuery['workspace']['doc']
>['grantedUsersList']['edges'][number]['node'];

export interface DeferredPermissionEditorController {
  commit(): Promise<void>;
}

interface DocPermissionsEditorProps {
  config?: PermissionEditorConfig;
  deferred?: boolean;
  docId: string;
  onDeferredController?: (
    controller: DeferredPermissionEditorController | null
  ) => void;
  onDraftEntriesChange?: (entries: readonly PermissionEntry[]) => void;
  onPermissionChanged?: (
    event: PermissionEditorChangeEvent
  ) => void | Promise<void>;
  presentation?: 'access' | 'full' | 'people';
}

const DOC_PERMISSION_CONFIG: PermissionEditorConfig = {
  allRoles: [
    DocRole.Owner,
    DocRole.Manager,
    DocRole.Editor,
    DocRole.Commenter,
    DocRole.Reader,
  ],
  grantableRoles: [
    DocRole.Manager,
    DocRole.Editor,
    DocRole.Commenter,
    DocRole.Reader,
  ],
  ownerRole: DocRole.Owner,
  demoteOnTransferRole: DocRole.Manager,
  defaultGrantRole: DocRole.Editor,
  roleLabelKeys: OBJECT_ROLE_LABEL_KEYS,
  allowGroups: true,
  testIdPrefix: 'doc-permissions',
};

function permissionKey(entry: Pick<PermissionEntry, 'targetId' | 'type'>) {
  return `${entry.type}:${entry.targetId}`;
}

function createDeferredPermissionAdapter(
  base: PermissionEditorAdapter,
  onCommitted: MutableRefObject<
    DocPermissionsEditorProps['onPermissionChanged']
  >,
  onDraftChanged: MutableRefObject<
    DocPermissionsEditorProps['onDraftEntriesChange']
  >
) {
  let originalEntries: PermissionEntry[] | null = null;
  let draftEntries: PermissionEntry[] = [];
  let members: Awaited<ReturnType<PermissionEditorAdapter['loadMembers']>> = [];
  let groups: Awaited<ReturnType<PermissionEditorAdapter['loadGroups']>> = [];

  const publishDraft = () => onDraftChanged.current?.([...draftEntries]);
  const ensureLoaded = async () => {
    if (originalEntries) return;
    originalEntries = await base.loadEntries();
    draftEntries = [...originalEntries];
    publishDraft();
  };

  const adapter: PermissionEditorAdapter = {
    async loadEntries() {
      await ensureLoaded();
      return [...draftEntries];
    },
    async loadMembers(query) {
      members = await base.loadMembers(query);
      return members;
    },
    async loadGroups() {
      groups = await base.loadGroups();
      return groups;
    },
    async grantUser(userId, role) {
      const member = members.find(candidate => candidate.id === userId);
      draftEntries = [
        ...draftEntries.filter(
          entry => permissionKey(entry) !== `user:${userId}`
        ),
        {
          avatarUrl: member?.avatarUrl ?? null,
          email: member?.email ?? '',
          id: `draft-user-${userId}`,
          name: member?.name ?? '',
          role,
          targetId: userId,
          type: 'user',
        },
      ];
      publishDraft();
    },
    async grantGroup(groupId, role) {
      const group = groups.find(candidate => candidate.id === groupId);
      draftEntries = [
        ...draftEntries.filter(
          entry => permissionKey(entry) !== `group:${groupId}`
        ),
        {
          id: `draft-group-${groupId}`,
          name: group?.name ?? groupId,
          role,
          targetId: groupId,
          type: 'group',
        },
      ];
      publishDraft();
    },
    async updateRole(entry, newRole) {
      draftEntries = draftEntries.map(candidate =>
        permissionKey(candidate) === permissionKey(entry)
          ? { ...candidate, role: newRole }
          : candidate
      );
      publishDraft();
    },
    async revoke(entry) {
      draftEntries = draftEntries.filter(
        candidate => permissionKey(candidate) !== permissionKey(entry)
      );
      publishDraft();
    },
  };

  const controller: DeferredPermissionEditorController = {
    async commit() {
      await ensureLoaded();
      const originalByKey = new Map(
        (originalEntries ?? []).map(entry => [permissionKey(entry), entry])
      );
      const draftByKey = new Map(
        draftEntries.map(entry => [permissionKey(entry), entry])
      );

      for (const entry of originalEntries ?? []) {
        if (draftByKey.has(permissionKey(entry))) continue;
        await base.revoke(entry);
        await onCommitted.current?.({
          action: 'revoke',
          role: null,
          targetEmail: entry.type === 'user' ? entry.email : undefined,
          targetId: entry.targetId,
          targetName: entry.name,
          type: entry.type,
        });
      }

      for (const entry of draftEntries) {
        const original = originalByKey.get(permissionKey(entry));
        const eventBase = {
          role: entry.role,
          targetEmail: entry.type === 'user' ? entry.email : undefined,
          targetId: entry.targetId,
          targetName: entry.name,
          type: entry.type,
        } as const;
        if (!original) {
          if (entry.type === 'user') {
            await base.grantUser(entry.targetId, entry.role);
          } else {
            await base.grantGroup(entry.targetId, entry.role);
          }
          await onCommitted.current?.({ action: 'grant', ...eventBase });
        } else if (original.role !== entry.role) {
          await base.updateRole(original, entry.role);
          await onCommitted.current?.({ action: 'update', ...eventBase });
        }
      }

      originalEntries = [...draftEntries];
    },
  };

  return { adapter, controller };
}

export const DocPermissionsEditor = ({
  config = DOC_PERMISSION_CONFIG,
  deferred = false,
  docId,
  onDeferredController,
  onDraftEntriesChange,
  onPermissionChanged,
  presentation = 'full',
}: DocPermissionsEditorProps) => {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const docMutationService = useService(DocMutationService);
  const workspaceId = workspaceService.workspace.id;
  const onCommittedRef = useRef(onPermissionChanged);
  const onDraftChangedRef = useRef(onDraftEntriesChange);
  onCommittedRef.current = onPermissionChanged;
  onDraftChangedRef.current = onDraftEntriesChange;

  const adapter = useMemo(
    (): PermissionEditorAdapter => ({
      async loadEntries(): Promise<PermissionEntry[]> {
        if (!serverService.server) return [];

        const [userResult, groupResult, groupsResult] = await Promise.all([
          serverService.server.gql({
            query: getPageGrantedUsersListQuery,
            variables: {
              workspaceId,
              docId,
              pagination: { first: 50 },
            },
          }),
          serverService.server.gql({
            query: getDocGroupRolesQuery,
            variables: { workspaceId, docId },
          }),
          serverService.server.gql({
            query: getGroupsQuery,
            variables: { workspaceId },
          }),
        ]);

        const edges = userResult.workspace?.doc?.grantedUsersList?.edges ?? [];
        const userEntries: PermissionEntry[] = edges.map(
          (edge: { node: GrantedUser }) => ({
            type: 'user' as const,
            id: edge.node.user.id,
            targetId: edge.node.user.id,
            name: edge.node.user.name ?? '',
            email: edge.node.user.email ?? '',
            avatarUrl: edge.node.user.avatarUrl ?? null,
            role: edge.node.role,
            isAreaBlocked: edge.node.isAreaBlocked,
            areaBoundaryKind: edge.node.areaBoundaryKind,
            areaBoundaryRootFolderId: edge.node.areaBoundaryRootFolderId,
            areaBoundaryRootName: edge.node.areaBoundaryRootName,
          })
        );

        const groupNameById = new Map(
          (groupsResult.groups ?? []).map(g => [g.id, g.name])
        );
        const groupEntries: PermissionEntry[] = (
          groupResult.docGroupRoles ?? []
        ).map(r => ({
          type: 'group' as const,
          id: r.id,
          targetId: r.groupId,
          name: groupNameById.get(r.groupId) ?? r.groupId,
          role: r.role,
          isAreaBlocked: r.isAreaBlocked,
          areaBoundaryKind: r.areaBoundaryKind,
          areaBoundaryRootFolderId: r.areaBoundaryRootFolderId,
          areaBoundaryRootName: r.areaBoundaryRootName,
        }));

        return [...userEntries, ...groupEntries];
      },

      async loadMembers(query: string) {
        if (!serverService.server) return [];

        const result = await serverService.server.gql({
          query: getMembersByWorkspaceIdQuery,
          variables: {
            workspaceId,
            take: 50,
            query: query || undefined,
          },
        });

        return (result.workspace?.members ?? []).map(member => ({
          id: member.id,
          name: member.name ?? '',
          email: member.email ?? '',
          avatarUrl: member.avatarUrl ?? null,
        }));
      },

      async loadGroups() {
        if (!serverService.server) return [];

        const result = await serverService.server.gql({
          query: getGroupsQuery,
          variables: { workspaceId },
        });
        return (result.groups ?? []).map(g => ({
          id: g.id,
          name: g.name,
          description: g.description ?? undefined,
        }));
      },

      async grantUser(userId: string, role: string) {
        await docMutationService.grantUserRoles({
          docId,
          userIds: [userId],
          role,
        });
      },

      async grantGroup(groupId: string, role: string) {
        await docMutationService.grantGroupRole({
          docId,
          groupId,
          role,
        });
      },

      async updateRole(entry: PermissionEntry, newRole: string) {
        if (entry.type === 'user') {
          await docMutationService.updateUserRole({
            docId,
            userId: entry.targetId,
            role: newRole,
          });
        } else {
          await docMutationService.grantGroupRole({
            docId,
            groupId: entry.targetId,
            role: newRole,
          });
        }
      },

      async revoke(entry: PermissionEntry) {
        if (entry.type === 'user') {
          await docMutationService.revokeUserRole({
            docId,
            userId: entry.targetId,
          });
        } else {
          await docMutationService.revokeGroupRole({
            docId,
            groupId: entry.targetId,
          });
        }
      },
    }),
    [docMutationService, serverService.server, workspaceId, docId]
  );

  const deferredState = useMemo(
    () =>
      deferred
        ? createDeferredPermissionAdapter(
            adapter,
            onCommittedRef,
            onDraftChangedRef
          )
        : null,
    [adapter, deferred]
  );

  useEffect(() => {
    onDeferredController?.(deferredState?.controller ?? null);
    return () => onDeferredController?.(null);
  }, [deferredState, onDeferredController]);

  const emptyMessage =
    t[
      'com.streamlain.settings.workspace.permissions.doc-direct-empty-folder-access-note'
    ]?.() ??
    'Keine direkten Freigaben. Zugriff kann zusätzlich über Ordnerfreigaben bestehen.';

  return (
    <PermissionEditor
      config={config}
      adapter={deferredState?.adapter ?? adapter}
      deferred={deferred}
      emptyMessage={emptyMessage}
      onPermissionChanged={
        deferred || !onPermissionChanged
          ? undefined
          : event => {
              void onPermissionChanged(event);
            }
      }
      presentation={presentation}
    />
  );
};
